import { ErrorMessage } from "@atlaskit/form";
import {
  Button,
  CloseIcon,
  RadioSelect,
  RealEstateMortgageSchema,
  RealEstate_InitialState,
  SearchableTextInput,
  Select,
  TextInput,
  banks,
  useOptions
} from "@fla/components";
import HouseIcon from "@fla/components/src/assets/svgs/house.svg";
import HouseDollarIcon from "@fla/components/src/assets/svgs/house_dollar.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { selectConsentOrdersBase } from "pages/ConsentOrders";
import { useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

interface IProps {
  formId: string;
  store: RealEstate_InitialState;
  onSubmit: (values: RealEstate_InitialState) => void;
}

export default function Mortgage({ formId, store, onSubmit }: IProps) {
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const { ownerV2Options } = useOptions({ initialDetails });

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm({
    mode: "all",
    defaultValues: store,
    resolver: yupResolver(RealEstateMortgageSchema)
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "mortgage"
  });

  const addMore = () => {
    append({ lender: "", value: "", owner: "" });
  };

  const removeMortgage = (idx: number) => {
    remove(idx);
  };

  return (
    <form id={formId} className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-8 pb-8 border-b flex flex-col items-center justify-center">
        <img src={HouseIcon} className="w-10 h-auto my-3" alt="mortgage" />
        <h3 className="text-center">Real Estate</h3>
        <p className="text-sm">Mortgage</p>
      </div>
      <RadioSelect
        title="Is there a mortgage on this property?"
        name="if_mortgage_on_property"
        id="if_mortgage_on_property"
        control={control}
        options={[
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" }
        ]}
        type="card"
        onChange={val => {
          if (val === "yes") {
            addMore();
          }
          if (val === "no") {
            replace([]);
          }
        }}
      />

      <div className="space-y-3">
        {Array.isArray(fields) && fields.length
          ? fields.map(({ id }, idx) => (
              <article key={id} className="w-full bg-[#f2f2f2] text-lg p-5 rounded-lg space-y-5">
                <div className="flex items-center justify-between pb-5 border-b">
                  <div className="flex items-center space-x-3">
                    <img src={HouseDollarIcon} className="w-8 h-auto" alt="mortgage" />
                    <p className="font-bold">Mortgage {idx + 1}</p>
                  </div>
                  {idx > 0 ? (
                    <button
                      type="button"
                      className="transition-all hover:bg-red-400/20 p-2 rounded-lg"
                      onClick={() => removeMortgage(idx)}
                    >
                      <CloseIcon strokeColor="text-red-500" title="remove child details" />
                    </button>
                  ) : null}
                </div>

                <SearchableTextInput
                  title="Who is the lender?"
                  control={control}
                  options={banks.map((b, idx) => ({
                    label: (
                      <div className="flex items-center justify-between" key={idx}>
                        <p className="text-input">{b.label}</p>
                        <img src={b.img} className="w-auto h-5" alt={b.img} />
                      </div>
                    ),
                    value: b.value
                  }))}
                  name={`mortgage.${idx}.lender`}
                  id={`mortgage.${idx}.lender`}
                  // error={
                  //   Array.isArray(errors.mortgage) && errors?.mortgage?.length
                  //     ? errors?.mortgage[idx]?.lender?.message
                  //     : ""
                  // }
                  placeholder="Start typing..."
                  theme="dark"
                />

                <TextInput
                  title="What is the current outstanding mortgage?"
                  subTitle="Enter the exact amount if possible. Otherwise estimate is okay."
                  control={control}
                  name={`mortgage.${idx}.value`}
                  id={`mortgage.${idx}.value`}
                  isAmount={true}
                  theme="dark"
                />
                <Select
                  title="Whose name is the mortgage in?"
                  subTitle="E.g in whose name is title in now?"
                  control={control}
                  placeholder="Select"
                  options={ownerV2Options}
                  name={`mortgage.${idx}.owner`}
                  id={`mortgage.${idx}.owner`}
                  theme="dark"
                />
              </article>
            ))
          : null}
      </div>

      {watch("if_mortgage_on_property") === "yes" ? (
        <>
          <div className="w-full py-10 border border-gray-300 border-dashed rounded-lg flex flex-col items-center justify-center space-y-4 mb-8">
            <Button
              className="px-5 py-2 uppercase"
              fontSize="sm"
              onClick={addMore}
              // appearance="outline"
              borderWidth="2"
            >
              + Add Another Mortgage
            </Button>
          </div>
          {errors?.mortgage?.message ? (
            <section className="flex justify-center">
              <ErrorMessage>{errors?.mortgage?.message}</ErrorMessage>
            </section>
          ) : null}
        </>
      ) : null}
    </form>
  );
}
